import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { useApiUrl, useDataProvider } from "@refinedev/core";
import { DeleteIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parseGoogleMapsUrl } from "@/lib/urlUtils";

export function MapsUrlField({ id, inputProps }: AutoFormFieldProps) {
  const form = useFormContext();
  const value = useWatch({ name: id });
  const venue = useWatch({ name: "venue" });
  const venueTba = useWatch({ name: "venueTba" });
  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();

  // `inputProps` is `{...register("mapsUrl")}`, so it carries react-hook-form's OWN `onBlur`.
  // Spread after ours (which it used to be), it silently replaced the coordinate lookup below —
  // so the map link never filled geolocation and a curator had to type it by hand. Take it out
  // of the spread and compose the two handlers instead.
  const { key, onChange, onBlur, ...restInputProps } = inputProps;

  const handleClear = () => {
    form.setValue(id, null, { shouldDirty: true });
    onChange({
      target: { name: id, value: null },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    const coords = await parseGoogleMapsUrl(e.target.value, async (url) => {
      const response = await custom?.({
        url: `${apiUrl}/events/parseCoords?url=${url}`,
        method: "get",
      });
      return (response?.data as any) ?? "";
    });
    if (coords) {
      form.setValue("geolocation", coords, { shouldDirty: true });
    }
  };

  return (
    <div className="flex space-x-1 items-end">
      <div className="w-11/12">
        <Input
          id={id}
          type="url"
          value={value ?? ""}
          disabled={!!venue || !!venueTba || restInputProps.disabled}
          onChange={onChange}
          onBlur={handleBlur}
          {...restInputProps}
        />
      </div>
      <Button className="w-1/12" type="button" onClick={handleClear}>
        <DeleteIcon className="w-4 h-4" />
      </Button>
    </div>
  );
}
